import { Card, CardHeader, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Separator } from "@/components/ui/separator";
import { ChevronDown, ChevronUp, Copy, LayoutGrid } from "lucide-react";
import { useState, useMemo } from "react";
import { useAppSelector } from "@common-reducers/hooks/store.hook";
import { selectCampaignLogGroupsByCampaignId, selectCampaignDetails } from "@common-reducers/CampaignSelectors";
import { CampaignLogTimeline } from "./CampaignLogTimeline";
import moment from "moment";

interface CampaignGroupCardProps {
    campaignId: string;
}

export function CampaignGroupCard({ campaignId }: CampaignGroupCardProps) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [copiedId, setCopiedId] = useState<string | null>(null);

    const logs = useAppSelector(state => selectCampaignLogGroupsByCampaignId(state, campaignId));
    const campaign = useAppSelector(state => selectCampaignDetails(state, campaignId));

    const handleCopyId = async (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        await navigator.clipboard.writeText(id);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    const stats = useMemo(() => {
        if (!logs.length) return null;

        const statusCounts: Record<string, number> = {};
        let errors = 0;

        logs.forEach(log => {
            statusCounts[log.status] = (statusCounts[log.status] || 0) + 1;
            if (log.status.includes('error') || log.status.includes('failed')) {
                errors++;
            }
        });

        return {
            total: logs.length,
            errors,
            latestLog: logs[0], // Logs are sorted by createdAt desc
            statusCounts
        };
    }, [logs]);

    if (!logs.length) return null;

    const allLogs = logs.flat();
    const latestLog = allLogs.length > 0 ? allLogs[0] : null;
    
    // CampaignLog.campaign is the bt-common Campaign snapshot (#53 —
    // typed via CampaignSchema.passthrough()). Consumer reads
    // template.name / campaignStatus, both on the canonical shape.
    const logCampaign = latestLog?.campaign;

    // Determine Campaign Name (Template Name)
    const templateName = campaign?.template?.name || logCampaign?.template?.name || 'no template';

    // Determine Status
    const status = logCampaign?.campaignStatus || campaign?.campaignStatus || 'unknown';

    const formatStatus = (s: string) => {
        return s.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    const getStatusColor = (s: string) => {
        if (s.includes('completed') || s.includes('delivered')) return 'bg-green-50 text-green-700 border-green-200';
        if (s.includes('paused')) return 'bg-yellow-50 text-yellow-700 border-yellow-200';
        if (s.includes('aborted') || s.includes('error')) return 'bg-red-50 text-red-700 border-red-200';
        return 'bg-blue-50 text-blue-700 border-blue-200';
    };

    return (
        <Card className="overflow-hidden border-slate-200 dark:border-slate-700">
            <CardHeader className="pb-3 bg-slate-50 dark:bg-slate-800/50">
                <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                        {/* Header Row */}
                        <div className="flex items-center gap-4 flex-wrap mb-2">
                            <div className="flex items-center gap-2">
                                <LayoutGrid className="h-5 w-5 text-slate-500" />
                                <h3 className="font-semibold text-slate-900 dark:text-slate-100">
                                    {templateName}
                                </h3>
                                <Badge variant="outline" className={getStatusColor(status)}>
                                    {formatStatus(status)}
                                </Badge>
                            </div>
                        </div>

                        {/* Details Row */}
                        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600 dark:text-slate-300">
                            <div className="flex items-center gap-2">
                                <span className="text-slate-500 dark:text-slate-400">Campaign ID:</span>
                                <code className="text-xs bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded font-mono text-slate-700 dark:text-slate-200">
                                    {campaignId}
                                </code>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0"
                                    onClick={(e) => handleCopyId(e, campaignId)}
                                >
                                    <Copy className={copiedId === campaignId ? "h-3 w-3 text-green-600" : "h-3 w-3 text-slate-400"} />
                                </Button>
                            </div>
                            <div className="text-slate-500 dark:text-slate-400 text-xs">
                                <span className="font-medium">Latest Activity: </span>
                                <span>{latestLog ? moment(latestLog.createdAt).format('MM/DD/YYYY, h:mm:ss A') : '-'}</span>
                            </div>
                        </div>
                    </div>
                    
                    <CollapsibleTrigger asChild>
                        <Button 
                            variant="ghost" 
                            size="sm" 
                            className="shrink-0"
                            onClick={() => setIsExpanded(!isExpanded)}
                        >
                            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </Button>
                    </CollapsibleTrigger>
                </div>
            </CardHeader>

            <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
                <CollapsibleContent>
                    <Separator className="bg-slate-200 dark:bg-slate-700" />
                    <CardContent className="pt-4 bg-slate-50/50 dark:bg-slate-900/50">
                        <div className="space-y-4">
                            <div className="flex items-center justify-between mb-3">
                                <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Timeline</h4>
                                <span className="text-xs text-slate-500 dark:text-slate-400">
                                    {allLogs.length} log{allLogs.length !== 1 ? 's' : ''}
                                </span>
                            </div>
                            <CampaignLogTimeline 
                                logs={allLogs} 
                                campaignId={campaignId} 
                            />
                        </div>
                    </CardContent>
                </CollapsibleContent>
            </Collapsible>
        </Card>
    );
}
